import { forEach, assign } from 'lodash';

import { resolve } from 'gear';

import { Matrix, Bounds, Boundary,
         AbsoluteAnchor, Shape } from 'cdl';

import { Tool, fastThrottle } from '../view';
import { EmptyPen } from '../../cdl/style';

export const AddFigureTool = Tool.extend({ typeName: 'AddFigureTool',

  type: 'Final',

  toolbarName: 'addFigureTool',

  useCursor: 'crosshair',

  initTool( view, figure ) {
    this._figure = figure;
    return figure.update$();
  },

  link( view ) {
    view.cursor = 'crosshair';
  },
  unlink( view ) {
    view.cursor = 'default';
  },

  get brush() {
    return this._figure.brush;
  },
  set brush( brush_ ) {
    const figure = this._figure;
    if( figure.isShape ) {
      forEach( figure.polyregions, p => p.brush = brush_ );
    }
    else {
      figure.brush = brush_;
    }
  },

  get pen() {
    return this._figure.pen;
  },
  set pen( pen_ ) {
    const figure = this._figure;
    if( figure.isShape ) {
      forEach( figure.polyregions, p => p.pen = pen_ );
    }
    else {
      figure.pen = pen_;
    }
  },

  cleanUpFigure( /*figure*/ ) {
    // ...
  },

  _commitFigure$() {
    const addedFigure = this._addedFigure;
    if( ! addedFigure ) {
      return resolve();
    }

    this.cleanUpFigure(addedFigure);
    this._addedFigure = null;
    return this.view.commit$();
  },

  _commit$() {
    return this._commitFigure$();
  },

  _cancel$( view ) {
    view.remove(this._addedFigure);
    return view.preview$();
  },

  on_pointerdown({ view, event, docPoint, which }) {
    const leftClick = which === 1 ;

    if( leftClick ) {
      event.stopPropagation();

      this._minSize = view.viewDeltaToDoc(10);

      this._posDoc = docPoint;
      this._downPosDoc = this._posDoc.clone();

      const figure = this._addedFigure = this._figure.clone();
      figure.bounds$() .then( bounds => {

      this._initialWidth  = view.viewDeltaToDoc(400);
      this._initialHeight = this._initialWidth * bounds.height/bounds.width;
      const initialBounds = Bounds.ofPoints([this._downPosDoc, this._downPosDoc.plus_(this._initialWidth,this._initialHeight)]);

      figure.fit$(initialBounds) .then( () => {

      this._originalMatrix22 = figure.matrix22.clone();
      this._originalPin      = figure.pin.clone();

      view.add(figure);

      this.on_pointermove(event);

      }); });
    }
  },

  on_pointermove: fastThrottle(function({ view, event, docPoint, cmdKey }) {
    const figure = this._addedFigure;
    if( ! figure ) {
      return;
    }

    this._posDoc = docPoint;

    this._ctrlKey = cmdKey;
    const t = this.computeTransform();

    figure.matrix22 = this._originalMatrix22.clone();
    figure.pin = this._originalPin.clone();

    figure.transform(t);

    view.preview$();

  }, 10),

  on_pointerup() {
    this._commitFigure$();
  },

  computeTransform() {
    const cp = this._posDoc, dp = this._downPosDoc ;
    const bounds = Bounds.ofPoints([cp,dp]);

    const minSize = this._minSize;

    const initialWidth  = this._initialWidth,
          initialHeight = this._initialHeight;

    let nextWidth  = bounds.width;
    let nextHeight = bounds.height;

    // Guard against to small figures
    if( nextWidth < minSize ) {
      nextWidth = minSize;
      if( cp.x < dp.x ) {
        bounds.lx = dp.x - minSize;
      }
    }
    if( nextHeight < minSize ) {
      nextHeight = minSize;
      if( cp.y < dp.y ) {
        bounds.ly = dp.y - minSize;
      }
    }

    let sx = nextWidth  / initialWidth,
        sy = nextHeight / initialHeight;

    if( this._ctrlKey ) {
      // Isometric locking
      if( sx > sy ) {
        sy = sx;
        if(cp.y < dp.y) {
          bounds.ly = dp.y - initialHeight * sy;
        }
      }
      else {
        sx = sy;
        if(cp.x < dp.x) {
          bounds.lx = dp.x - initialWidth * sx;
        }
      }
    }

    const { lx_ly } = bounds;
    const transform = Matrix.m( Matrix.scaleFrom( lx_ly, sx,sy ),
                                Matrix.translate( lx_ly .minus( dp ) ) );
    return transform;
  }
});

Tool.AddFigure = AddFigureTool;

export const DrawCurveTool = AddFigureTool.extend({ typeName: 'DrawCurveTool',
  initTool( view, curve, config = {} ) {
    if( ! config.pen ) {
      config.pen = EmptyPen();
    }
    if( ! config.brush ) {
      config.brush = Tool.defaultBrush();
    }
    const figure = Shape.createFromCurve( curve, config );

    this.callParent(view,figure,config);
  },
  cleanUpFigure( figure ) {
    // Apply transform to the polyregion
    const matrix = figure._computeMatrix();

    const polyregion = figure.polyregions[0];

    polyregion.transform(matrix);
    polyregion.renderFrame = null;
    polyregion.renderMatrix = null;

    const center = polyregion.bounds.center;
    figure.anchor = AbsoluteAnchor(center.clone());
    figure.matrix22 = new Matrix();
    figure.pin = center.clone();
  }
});

export const DrawRectangleTool = DrawCurveTool.extend({ typeName: 'DrawRectangleTool',
  toolbarName: 'drawRectangleTool',
  initTool( view, config ) {
    const curve = Boundary.createRectangle(assign({ width: 500, height: 500 },config));
    this.callParent(view,curve,config);
  }
});
Tool.DrawRectangle = DrawRectangleTool;

export const DrawEllipseTool = DrawCurveTool.extend({ typeName: 'DrawEllipseTool',
  toolbarName: 'drawEllipseTool',
  initTool( view, config ) {
    const curve = Boundary.createEllipse(assign({ width: 500, height: 500 },config));
    this.callParent(view,curve,config);
  }
});
Tool.DrawEllipse = DrawEllipseTool;



/** WEBPACK FOOTER **
 ** ./src/ui/tool/add-figure.js
 **/